/**
 * SetrixServer entity class
 */

/**
 * SetrixServerFactory
 * returns SetrixServer entity class
 *
 * @class SetrixServerFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.SetrixServer} class
 *
 * @ngdoc factory
 * @name mwApp.factory:SetrixServerFactory
 */
/* @ngInject */
export default function SetrixServerFactory ($http, alertLiteSvc) {
  function SetrixServer () {}

  const base = '/nora/api/content-delivery/servers'
  const getRequestConfig = (networkId, multiorgEnabled) => (multiorgEnabled && networkId != null)
    ? { params: { networkId } }
    : {}

  const getUrl = (suffix = '') => `${base}${suffix}`

  SetrixServer.loadAll = function (networkId, multiorgEnabled = false) {
    return $http.get(getUrl(), getRequestConfig(networkId, multiorgEnabled))
  }

  SetrixServer.verify = function (networkId, data, multiorgEnabled = false) {
    return $http.post(getUrl('/verify'), data, getRequestConfig(networkId, multiorgEnabled))
  }

  SetrixServer.saveAll = function (networkId, servers, multiorgEnabled = false) {
    const includeNetworkId = !!multiorgEnabled && networkId != null

    const payload = servers && typeof servers === 'object' && !Array.isArray(servers)
      ? (includeNetworkId ? { ...servers, networkId } : { ...servers })
      : (includeNetworkId
        ? { networkId, create: Array.isArray(servers) ? servers : [], update: [], delete: [] }
        : { create: Array.isArray(servers) ? servers : [], update: [], delete: [] })

    const config = getRequestConfig(networkId, multiorgEnabled)
    return $http.post(getUrl(), payload, config)
      .then(() => $http.get(getUrl(), config))
  }

  SetrixServer.remove = function (networkId, id, multiorgEnabled = false) {
    return $http.delete(getUrl(`/${id}`), getRequestConfig(networkId, multiorgEnabled))
  }

  SetrixServer.loadStreamTemplates = function (serverId, networkId, multiorgEnabled = false) {
    if (!serverId) return Promise.resolve({ data: { payload: [] } })
    return $http.get(getUrl(`/${serverId}/streams/templates`), getRequestConfig(networkId, multiorgEnabled))
      .then((response) => response?.data?.payload?.content || [])
      .catch((rejection) => {
        alertLiteSvc.alertError(rejection?.statusText || 'Failed to load templates')
      })
  }

  /**
   * POST /content-delivery/servers/{serverId}/streams — create stream from template.
   * Body: { templateExternalId, fields: [{ id, value, type }, …] } (see API schema).
   *
   * @param {string|number} serverId
   * @param {Object} body
   * @param {number|null} networkId
   * @param {boolean} [multiorgEnabled]
   * @returns {Promise}
   */
  SetrixServer.createStream = function (serverId, body, networkId, multiorgEnabled = false) {
    if (!serverId) {
      return Promise.reject(new Error('Missing server id'))
    }
    return $http.post(
      getUrl(`/${serverId}/streams`),
      body || {},
      getRequestConfig(networkId, multiorgEnabled)
    )
  }

  function postStreamCommand (serverId, streamId, command, networkId, multiorgEnabled) {
    if (!serverId || streamId == null) {
      return Promise.reject(new Error('Missing server or stream id'))
    }
    const streamSegment = encodeURIComponent(String(streamId))
    return $http.post(
      getUrl(`/${serverId}/streams/${streamSegment}/${command}`),
      {},
      getRequestConfig(networkId, multiorgEnabled)
    )
  }

  SetrixServer.restartStream = function (serverId, streamId, networkId, multiorgEnabled = false) {
    return postStreamCommand(serverId, streamId, 'restart', networkId, multiorgEnabled)
  }

  SetrixServer.startStream = function (serverId, streamId, networkId, multiorgEnabled = false) {
    return postStreamCommand(serverId, streamId, 'start', networkId, multiorgEnabled)
  }

  SetrixServer.stopStream = function (serverId, streamId, networkId, multiorgEnabled = false) {
    return postStreamCommand(serverId, streamId, 'stop', networkId, multiorgEnabled)
  }

  /**
   * DELETE /content-delivery/servers/{serverId}/streams/{bindingId} — remove content stream binding (bindingId = row id from GET …/streams).
   */
  SetrixServer.deleteStreamBinding = function (serverId, bindingId, networkId, multiorgEnabled = false) {
    if (!serverId || bindingId == null || bindingId === '') {
      return Promise.reject(new Error('Missing server or stream binding id'))
    }
    const idSegment = encodeURIComponent(String(bindingId))
    return $http.delete(
      getUrl(`/${serverId}/streams/${idSegment}`),
      getRequestConfig(networkId, multiorgEnabled)
    )
  }

  /**
   * GET /content-delivery/servers/streams — paginated content streams (overview / transcoding status).
   *
   * @param {Object} queryParams query string fields (page, count, sort-by, sort-order, content-type, q,
   *   stream-status [ENABLED|DISABLED|SCHEDULED], stream-state [DONE|ERROR|NOT_AVAILABLE|PROCESSING|TRANSITION|WAITING],
   *   serverIds, networkIds (comma-separated organization / network ids), …)
   * @param {number} [networkId] merged when multi-org uses network-scoped requests
   * @param {boolean} [multiorgEnabled]
   * @returns {Promise}
   */
  SetrixServer.loadStreamsOverview = function (queryParams, networkId, multiorgEnabled = false) {
    const config = getRequestConfig(networkId, multiorgEnabled)
    const params = { ...queryParams, ...(config.params || {}) }
    return $http.get(getUrl('/streams'), { params })
  }

  function mapStreamsExtraServersToFilterOptions (servers) {
    const list = Array.isArray(servers) ? servers : []
    return list
      .filter((s) => s && s.id != null)
      .map((s) => ({
        key: String(s.id),
        value: s.name || String(s.id)
      }))
  }

  function mapStreamsExtraNetworksToFilterOptions (networks) {
    const list = Array.isArray(networks) ? networks : []
    return list
      .filter((n) => n && (n.id != null || n.networkId != null))
      .map((n) => {
        const id = n.id != null ? n.id : n.networkId
        const name = n.name != null ? n.name : (n.networkName != null ? n.networkName : '')
        return {
          key: String(id),
          value: name || String(id)
        }
      })
  }

  /**
   * GET /content-delivery/servers/streams/extra — filter options for streams overview (no query params).
   * Resolves to parsed `{ networks, serverFilterList }` from `payload`.
   *
   * @returns {Promise<{ networks: Array, organizationFilterList: Array<{ key: string, value: string }>, serverFilterList: Array<{ key: string, value: string }> }>}
   */
  SetrixServer.loadStreamsExtra = function () {
    return $http.get(getUrl('/streams/extra'))
      .then((response) => {
        const payload = response?.data?.payload
        const networks = Array.isArray(payload?.networks) ? payload.networks : []
        const servers = payload?.servers
        return {
          networks,
          organizationFilterList: mapStreamsExtraNetworksToFilterOptions(networks),
          serverFilterList: mapStreamsExtraServersToFilterOptions(servers)
        }
      })
  }

  return SetrixServer
}
